import { CARD_OVERLAY, FACE_OVAL } from '../config.js';
import type { Rect } from '../quality/metrics.js';
import { el } from './dom.js';

/**
 * Capture screen — §4 layout: portrait full-screen preview, landscape card frame in the
 * centre, dimmed surroundings (§12 Option A — preview only; the recording is untouched),
 * status chips, guidance message, timer and progress bar.
 */

export interface CaptureScreenRefs {
  root: HTMLElement;
  video: HTMLVideoElement;
  setStep(text: string): void;
  setTitle(text: string): void;
  setChips(chips: Array<{ label: string; on: boolean }>): void;
  setMessage(text: string, tone?: 'info' | 'warn' | 'ok'): void;
  setTimer(text: string): void;
  setProgress(ratio: number): void;
  setCountdown(text: string | null): void;
  /** Diagnostic line (only rendered when the page is opened with #debug). */
  setDebug(text: string): void;
  onRetake(handler: () => void): void;
  drawOverlay(): void;
  /** Overlay geometry mapped into analysis-frame coordinates (§7 shared space). */
  overlayRectFor(frameW: number, frameH: number): Rect;
  dispose(): void;
}

export function createCaptureScreen(kind: 'card' | 'face', mirrored: boolean): CaptureScreenRefs {
  const video = el('video', { playsinline: 'true', autoplay: 'true', muted: 'true' });
  video.muted = true;
  if (mirrored) video.classList.add('mirrored');
  const overlay = el('canvas', { class: 'overlay' });

  const step = el('div', { class: 'step' });
  const title = el('div', { class: 'title' });
  const chips = el('div', { class: 'chips' });
  const debugLine = el('div', { class: 'debug-line' });
  const debugEnabled = window.location.hash.includes('debug') || window.location.search.includes('debug');
  const message = el('div', { class: 'message', text: '' });
  const timer = el('div', { class: 'timer' });
  const progressFill = el('div');
  const progress = el('div', { class: 'progress' }, [progressFill]);
  const countdown = el('div', { class: 'countdown' });
  const retakeBtn = el('button', { class: 'btn secondary', text: 'Retake' });

  const root = el('div', { class: 'capture-root' }, [
    video,
    overlay,
    el('div', { class: 'hud top' }, [step, title, chips, debugLine]),
    el('div', { class: 'hud bottom' }, [message, timer, progress, el('div', { class: 'controls' }, [retakeBtn])]),
    countdown,
  ]);

  let retakeHandler: (() => void) | null = null;
  retakeBtn.addEventListener('click', () => retakeHandler?.());

  /**
   * §7 — one canonical geometry: the overlay box is defined in relative coordinates and
   * mapped identically to the preview canvas and to the analysis frame. The preview uses
   * object-fit: cover, so the visible region equals the centre crop of the frame; both
   * mappings below use the same relative box, keeping analyzer and user view aligned.
   */
  const relativeBox = (viewportAspect: number): { x: number; y: number; w: number; h: number } => {
    if (kind === 'card') {
      const w = CARD_OVERLAY.widthRatio;
      // Card must render with the ISO ID-1 aspect on screen, whatever the viewport is.
      const h = (w / CARD_OVERLAY.cardAspect) * viewportAspect;
      return { x: (1 - w) / 2, y: 0.45 - h / 2, w, h };
    }
    return {
      x: 0.5 - FACE_OVAL.widthRatio / 2,
      y: FACE_OVAL.centerYRatio - FACE_OVAL.heightRatio / 2,
      w: FACE_OVAL.widthRatio,
      h: FACE_OVAL.heightRatio,
    };
  };

  const drawOverlay = () => {
    const w = (overlay.width = overlay.clientWidth * devicePixelRatio);
    const h = (overlay.height = overlay.clientHeight * devicePixelRatio);
    const ctx = overlay.getContext('2d');
    if (!ctx || !w || !h) return;
    ctx.clearRect(0, 0, w, h);
    // §12 — dim the area outside the card/oval on the PREVIEW only (recording untouched).
    // Kept light so the overall preview still looks bright and clear.
    ctx.fillStyle = 'rgba(0,0,0,0.42)';
    ctx.fillRect(0, 0, w, h);

    const r = relativeBox(w / h);
    const bx = r.x * w;
    const by = r.y * h;
    const bw = r.w * w;
    const bh = r.h * h;

    ctx.globalCompositeOperation = 'destination-out';
    if (kind === 'card') {
      roundRect(ctx, bx, by, bw, bh, 16 * devicePixelRatio);
    } else {
      ctx.beginPath();
      ctx.ellipse(bx + bw / 2, by + bh / 2, bw / 2, bh / 2, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';

    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 3 * devicePixelRatio;
    if (kind === 'card') {
      drawCorners(ctx, bx, by, bw, bh, 26 * devicePixelRatio);
    } else {
      ctx.beginPath();
      ctx.ellipse(bx + bw / 2, by + bh / 2, bw / 2, bh / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  return {
    root,
    video,
    setStep: (t) => (step.textContent = t),
    setTitle: (t) => (title.textContent = t),
    setChips: (items) => {
      chips.replaceChildren(
        ...items.map((c) => el('span', { class: `chip${c.on ? ' on' : ''}`, text: `${c.on ? '✓ ' : ''}${c.label}` })),
      );
    },
    setMessage: (t, tone = 'info') => {
      message.textContent = t;
      message.className = `message${tone === 'warn' ? ' warn' : tone === 'ok' ? ' ok' : ''}`;
    },
    setTimer: (t) => (timer.textContent = t),
    setProgress: (ratio) => (progressFill.style.width = `${Math.round(Math.min(1, Math.max(0, ratio)) * 100)}%`),
    setCountdown: (t) => (countdown.textContent = t ?? ''),
    setDebug: (t) => {
      if (debugEnabled) debugLine.textContent = t;
    },
    onRetake: (h) => (retakeHandler = h),
    drawOverlay,
    overlayRectFor: (frameW, frameH) => {
      // §7 — map the drawn viewport box through the object-fit:cover transform into
      // full-frame coordinates, then scale to the analysis frame. Without this the
      // analyzer would gate a slightly different region than the user sees.
      const vw = video.clientWidth || 1;
      const vh = video.clientHeight || 1;
      const fw = video.videoWidth || frameW;
      const fh = video.videoHeight || frameH;
      const scale = Math.max(vw / fw, vh / fh);
      const offsetX = (fw * scale - vw) / 2;
      const offsetY = (fh * scale - vh) / 2;
      const r = relativeBox(vw / vh);
      const vx = r.x * vw;
      const vy = r.y * vh;
      const vwBox = r.w * vw;
      const vhBox = r.h * vh;
      const toFrame = frameW / fw;
      return {
        x: ((vx + offsetX) / scale) * toFrame,
        y: ((vy + offsetY) / scale) * toFrame,
        w: (vwBox / scale) * toFrame,
        h: (vhBox / scale) * toFrame,
      };
    },
    dispose: () => root.remove(),
  };
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fill();
}

function drawCorners(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, len: number): void {
  const segments: Array<[number, number, number, number, number, number]> = [
    [x, y + len, x, y, x + len, y],
    [x + w - len, y, x + w, y, x + w, y + len],
    [x + w, y + h - len, x + w, y + h, x + w - len, y + h],
    [x + len, y + h, x, y + h, x, y + h - len],
  ];
  for (const [x1, y1, x2, y2, x3, y3] of segments) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.stroke();
  }
}
